import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, LogOut, Radio, Bitcoin, Copy, ExternalLink, RefreshCw, Activity } from "lucide-react";
import AdminSetBalance from "./AdminSetBalance";
import AdminWalletOps from "./AdminWalletOps";

const PREVIEW_API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LIVE_API = "https://aureontrader.com/api";

// Deterministic demo users for seminar presentations (seeded — never flickers)
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
const DEMO_FIRST = ["james", "amina", "chen", "fatima", "omar", "sofia", "lucas", "priya", "kofi", "elena", "david", "zara", "marcus", "layla", "ivan", "nadia", "peter", "aisha", "diego", "mei", "samuel", "ines", "yusuf", "clara", "omar", "lena", "raj", "tara", "viktor", "noor"];
const DEMO_LAST = ["k", "m", "a", "s", "r", "l", "t", "n", "b", "h", "o", "d", "e", "g", "p", "w"];
const DEMO_DOMAINS = ["gmail.com", "outlook.com", "yahoo.com", "icloud.com", "proton.me"];
const DEMO_TIERS = [100, 100, 250, 250, 500, 500, 1000];

function buildDemoUsers() {
  const rnd = mulberry32(20170826);
  const users = [];
  for (let i = 0; i < 250; i++) {
    const paid = i < 146; // first 146 are paid customers
    const name = `${DEMO_FIRST[Math.floor(rnd() * DEMO_FIRST.length)]}.${DEMO_LAST[Math.floor(rnd() * DEMO_LAST.length)]}${Math.floor(rnd() * 900 + 100)}`;
    const tier = paid ? DEMO_TIERS[Math.floor(rnd() * DEMO_TIERS.length)] : null;
    users.push({
      code: `AUR-${(0x10000000 + Math.floor(rnd() * 0xffffffff)).toString(16).toUpperCase().slice(0, 8)}`,
      email: `${name}@${DEMO_DOMAINS[Math.floor(rnd() * DEMO_DOMAINS.length)]}`,
      tier_paid: tier,
      balance_usd: paid ? Math.round((tier * (0.6 + rnd() * 2.2)) * 100) / 100 : null,
      referral_count: paid && rnd() > 0.55 ? Math.floor(rnd() * 9) + 1 : 0,
      referral_earned: 0,
      daily_redemptions: paid ? Math.floor(rnd() * 21) : 0,
      auto_trade: paid && rnd() > 0.5,
      created_at: null,
    });
  }
  users.forEach((u) => { u.referral_earned = u.referral_count * 25; });
  users.sort((a, b) => (b.balance_usd || 0) - (a.balance_usd || 0));
  return users;
}
const DEMO_USERS = buildDemoUsers();
const SHOWCASE_STATS = { total_users: 250, paid_customers: 146, total_available: 256_000_000, pending_withdrawals: 3 };

export default function Admin() {
  // /admin?live=1 → monitor the PRODUCTION site (works while deploys are blocked)
  const liveMode = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("live") === "1";
  const API = liveMode ? LIVE_API : PREVIEW_API;
  const [liveLimited, setLiveLimited] = useState(false);
  const [token, setToken] = useState(() => sessionStorage.getItem("aureon_admin_token") || null);
  const [password, setPassword] = useState("");
  const [rows, setRows] = useState(null);
  const [daily, setDaily] = useState(null);
  const [wdRows, setWdRows] = useState(null);
  const [usersData, setUsersData] = useState(null);
  const [cryptoRows, setCryptoRows] = useState(null);
  const [cryptoBusy, setCryptoBusy] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showcase, setShowcase] = useState(false);

  const load = async (tok) => {
    const headers = { Authorization: `Bearer ${tok}` };
    try {
      const [tx, dc, wd] = await Promise.all([
        axios.get(`${API}/admin/transactions`, { headers }),
        axios.get(`${API}/admin/daily-code`, { headers }),
        axios.get(`${API}/admin/withdrawals`, { headers }),
      ]);
      setRows(tx.data.transactions || []);
      setDaily(dc.data);
      setWdRows(wd.data.withdrawals || []);
      setError(null);
      // Crypto payment queue — only exists on the new build; ignore 404 silently
      try {
        const cp = await axios.get(`${API}/admin/crypto/list`, { headers });
        setCryptoRows(cp.data.payments || []);
      } catch {
        setCryptoRows([]);
      }
      // /admin/users exists only on the new build; on the live (older) build,
      // derive the user overview from paid transactions instead
      try {
        const us = await axios.get(`${API}/admin/users`, { headers });
        setUsersData(us.data);
      } catch {
        setLiveLimited(true);
        const txs = tx.data.transactions || [];
        const paid = txs.filter((t) => t.payment_status === "paid");
        const byCode = new Map();
        for (const t of paid) {
          if (t.access_code && !byCode.has(t.access_code)) {
            byCode.set(t.access_code, {
              code: t.access_code, email: t.email, tier_paid: t.amount,
              balance_usd: null, referral_count: 0, referral_earned: 0,
              daily_redemptions: null, auto_trade: false, created_at: t.created_at,
            });
          }
        }
        setUsersData({
          users: [...byCode.values()].reverse(),
          stats: {
            total_users: byCode.size,
            paid_customers: paid.length,
            total_revenue: paid.reduce((s, t) => s + (t.amount || 0), 0),
            pending_withdrawals: (wd.data.withdrawals || []).filter((w) => w.status === "pending").length,
          },
        });
      }
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem("aureon_admin_token");
        setToken(null);
      } else {
        setError("Could not load admin data.");
      }
    }
  };

  const regenerateCode = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/daily-code/regenerate`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      setDaily((d) => ({ ...d, code: data.code, is_active: true }));
    } finally {
      setBusy(false);
    }
  };

  const toggleCode = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/daily-code/toggle`,
        { active: !daily?.is_active },
        { headers: { Authorization: `Bearer ${token}` } });
      setDaily((d) => ({ ...d, is_active: data.is_active }));
    } finally {
      setBusy(false);
    }
  };

  const broadcastNow = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/daily-code/broadcast`, { force: !!daily?.broadcast_at },
        { headers: { Authorization: `Bearer ${token}` } });
      setTimeout(() => load(token), 4000);
    } catch (e) {
      setError(e.response?.data?.detail || "Broadcast failed to start.");
    } finally {
      setBusy(false);
    }
  };

  const [manualForm, setManualForm] = useState({ code: "", amount: "", wallet: "", txid: "" });
  const [manualBusy, setManualBusy] = useState(false);
  const [manualMsg, setManualMsg] = useState(null);
  const [manualErr, setManualErr] = useState(null);

  const submitManualPayout = async (e) => {
    e.preventDefault();
    setManualBusy(true);
    setManualMsg(null);
    setManualErr(null);
    try {
      const amountNum = parseFloat(manualForm.amount);
      if (!Number.isFinite(amountNum) || amountNum <= 0) {
        throw new Error("Amount must be a positive number");
      }
      const body = {
        code: manualForm.code.trim().toUpperCase(),
        amount: amountNum,
        wallet: manualForm.wallet.trim() || null,
        txid: manualForm.txid.trim() || null,
      };
      const { data } = await axios.post(`${API}/admin/payout/manual`, body,
        { headers: { Authorization: `Bearer ${token}` } });
      setManualMsg(`Logged · ${data.code} debited $${data.amount.toFixed(2)} · new balance $${data.new_balance.toFixed(2)}${data.email_sent ? " · receipt sent" : ""}`);
      setManualForm({ code: "", amount: "", wallet: "", txid: "" });
      load(token);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setManualErr(typeof d === "string" ? d : (err.message || "Manual payout failed."));
    } finally {
      setManualBusy(false);
    }
  };

  const [digestBusy, setDigestBusy] = useState(false);
  const [digestPreview, setDigestPreview] = useState(null);
  const [digestMsg, setDigestMsg] = useState(null);
  const [digestErr, setDigestErr] = useState(null);
  const [rateInfo, setRateInfo] = useState(null);
  const [purgeBusy, setPurgeBusy] = useState(false);
  const [purgeMsg, setPurgeMsg] = useState(null);
  const [benefitRows, setBenefitRows] = useState(null);
  const [activityRows, setActivityRows] = useState(null);
  const [activityFilter, setActivityFilter] = useState("all");
  const [activityCodeFilter, setActivityCodeFilter] = useState("");

  const loadActivity = async (tok) => {
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (activityFilter && activityFilter !== "all") params.set("kind", activityFilter);
      if (activityCodeFilter.trim()) params.set("code", activityCodeFilter.trim().toUpperCase());
      const { data } = await axios.get(`${API}/admin/activity?${params.toString()}`,
        { headers: { Authorization: `Bearer ${tok || token}` } });
      setActivityRows(data.activity || []);
    } catch {
      setActivityRows([]);
    }
  };

  const loadBenefits = async (tok) => {
    try {
      const { data } = await axios.get(`${API}/admin/benefit-redemptions`,
        { headers: { Authorization: `Bearer ${tok || token}` } });
      setBenefitRows(data.redemptions || []);
    } catch { setBenefitRows([]); }
  };

  const loadRate = async (tok) => {
    try {
      const { data } = await axios.get(`${API}/admin/boost-rate`,
        { headers: { Authorization: `Bearer ${tok || token}` } });
      setRateInfo(data);
    } catch {}
  };

  const purgeAbandoned = async () => {
    if (!window.confirm("Delete all abandoned / non-paid transactions from the log? This keeps only paid customers.")) return;
    setPurgeBusy(true);
    setPurgeMsg(null);
    try {
      const { data } = await axios.post(`${API}/admin/transactions/purge-abandoned`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      setPurgeMsg(`Removed ${data.deleted_non_paid} abandoned + ${data.deleted_junk} junk row(s).`);
      load(token);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setPurgeMsg(typeof d === "string" ? d : "Purge failed.");
    } finally {
      setPurgeBusy(false);
    }
  };

  const previewDigest = async () => {
    setDigestBusy(true);
    setDigestErr(null);
    setDigestMsg(null);
    try {
      const { data } = await axios.get(`${API}/admin/digest/preview`,
        { headers: { Authorization: `Bearer ${token}` } });
      setDigestPreview(data);
      if (!data.whatsapp_configured) {
        setDigestErr("WhatsApp not configured — set CALLMEBOT_APIKEY in backend/.env to enable auto-send at 20:00 Dubai.");
      }
    } catch (e) {
      setDigestErr(e?.response?.data?.detail || "Preview failed.");
    } finally {
      setDigestBusy(false);
    }
  };

  const sendDigestNow = async () => {
    setDigestBusy(true);
    setDigestErr(null);
    setDigestMsg(null);
    try {
      const { data } = await axios.post(`${API}/admin/digest/send-now`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      const sentCount = (data.results || []).filter((r) => r.sent).length;
      setDigestMsg(`Sent ${sentCount}/${data.messages} message(s) to WhatsApp.`);
      if (!data.whatsapp_configured) {
        setDigestErr("WhatsApp not configured — set CALLMEBOT_APIKEY in backend/.env.");
      }
    } catch (e) {
      setDigestErr(e?.response?.data?.detail || "Send failed.");
    } finally {
      setDigestBusy(false);
    }
  };

  const setWdStatus = async (wid, status) => {
    let txid = null;
    if (status === "paid") {
      txid = (window.prompt("Paste the TRC-20 payout transaction hash (TXID) — the client's receipt will include a live Tronscan link. Leave empty to skip.", "") || "").trim() || null;
      if (txid && !/^[0-9a-fA-F]{64}$/.test(txid)) {
        if (!window.confirm(`"${txid}" doesn't look like a 64-char TRC-20 TXID. Save anyway?`)) return;
      }
    }
    try {
      await axios.post(`${API}/admin/withdrawals/status`, { wid, status, txid },
        { headers: { Authorization: `Bearer ${token}` } });
      setWdRows((rows) => rows.map((w) => (w.wid === wid ? { ...w, status, payout_txid: txid || w.payout_txid } : w)));
    } catch (e) {
      console.error(e);
    }
  };

  const cryptoAction = async (sid, action) => {
    setCryptoBusy(`${sid}:${action}`);
    try {
      const path = action === "approve" ? "approve"
                : action === "reject"  ? "reject"
                : "recheck";
      const { data } = await axios.post(
        `${API}/admin/crypto/${path}`,
        { submission_id: sid },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setCryptoRows((rows) =>
        rows.map((r) =>
          r.id !== sid ? r
            : { ...r, status: data.status || r.status,
                access_code: data.access_code || r.access_code,
                auto_verified: data.auto_verified ?? r.auto_verified,
                verify_last: data.verify || r.verify_last }
        ),
      );
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === "string" ? d : "Crypto action failed.");
    } finally {
      setCryptoBusy(null);
    }
  };

  useEffect(() => {
    if (token) { load(token); loadRate(token); loadBenefits(token); loadActivity(token); }
  }, [token]);

  // Auto-refresh activity feed every 15s so the admin can see client actions in near-realtime
  useEffect(() => {
    if (!token) return;
    const t = setInterval(() => loadActivity(token), 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, activityFilter, activityCodeFilter]);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("aureon_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      setError("Invalid password.");
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    sessionStorage.removeItem("aureon_admin_token");
    setToken(null);
    setRows(null);
  };

  if (!token) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-ink px-6">
        <p className="mb-4 text-[10px] uppercase tracking-[0.3em] text-gold">Aureon · Owner</p>
        <h1 className="font-serif text-4xl font-light tracking-tight text-white" data-testid="admin-login-title">
          Payment <span className="italic text-gold">log.</span>
        </h1>
        <form onSubmit={login} className="mt-10 w-full max-w-xs" data-testid="admin-login-form">
          <input
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            className="w-full border border-white/15 bg-surface px-5 py-3.5 text-sm text-white placeholder:text-white/30 focus:border-gold focus:outline-none"
          />
          <button
            data-testid="admin-login-btn"
            type="submit"
            disabled={busy || !password}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-xs uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-gold-light disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Open the log"}
          </button>
          {error && (
            <p data-testid="admin-login-error" className="mt-4 text-center text-xs text-[#FF003C]">
              {error}
            </p>
          )}
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink px-6 py-16 lg:px-10">
      <div className="mx-auto max-w-6xl">
        <div className="flex items-end justify-between">
          <div>
            <p className="mb-3 text-[10px] uppercase tracking-[0.3em] text-gold">Aureon · Owner</p>
            <h1 className="font-serif text-4xl font-light tracking-tight text-white" data-testid="admin-log-title">
              Payment <span className="italic text-gold">log.</span>
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="admin-showcase-toggle"
              onClick={() => setShowcase((s) => !s)}
              className={`rounded-full px-4 py-1.5 text-[9px] uppercase tracking-[0.25em] transition-colors duration-300 ${
                showcase ? "bg-gold text-ink" : "border border-white/15 text-white/50 hover:text-gold"
              }`}
            >
              {showcase ? "Presentation on" : "Presentation"}
            </button>
            {liveMode && (
              <span className="flex items-center gap-2 rounded-full border border-[#00FF41]/40 px-4 py-1.5 text-[9px] uppercase tracking-[0.25em] text-[#00FF41]" data-testid="admin-live-badge">
                <Radio className="h-3 w-3 animate-pulse" />
                Live site data
              </span>
            )}
            <button
              data-testid="admin-logout-btn"
            onClick={logout}
            className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/50 transition-colors duration-300 hover:text-gold"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
          </div>
        </div>

        {/* ─── Owner · Set terminal balance override ─────────────────── */}
        <AdminSetBalance apiBase={API} token={token} />
        <AdminWalletOps apiBase={API} token={token} />

        {/* ─── Live client activity feed ─────────────────────────────── */}
        <div className="mt-10 border border-gold/25 bg-surface p-6" data-testid="admin-activity-card">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-gold" />
              <p className="text-[10px] uppercase tracking-[0.28em] text-gold">Live client activity</p>
              <span className="flex items-center gap-1.5 rounded-full border border-[#00FF41]/40 px-2.5 py-0.5 text-[9px] uppercase tracking-[0.22em] text-[#00FF41]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#00FF41] animate-pulse" />
                Live · 15s
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <input
                data-testid="admin-activity-code-filter"
                value={activityCodeFilter}
                onChange={(e) => setActivityCodeFilter(e.target.value.toUpperCase())}
                onBlur={() => loadActivity(token)}
                onKeyDown={(e) => { if (e.key === "Enter") loadActivity(token); }}
                placeholder="Filter by AUR-code"
                className="w-40 border border-white/10 bg-ink px-3 py-1.5 font-tabular text-[10px] text-white placeholder:text-white/25 focus:border-gold focus:outline-none"
              />
              <select
                data-testid="admin-activity-kind-filter"
                value={activityFilter}
                onChange={(e) => { setActivityFilter(e.target.value); setTimeout(() => loadActivity(token), 50); }}
                className="border border-white/10 bg-ink px-3 py-1.5 font-tabular text-[10px] text-white/80 focus:border-gold focus:outline-none"
              >
                <option value="all">All events</option>
                <option value="login">Logins</option>
                <option value="trade">Trades</option>
                <option value="boost_claimed">Boost claims</option>
                <option value="withdrawal_requested">Withdrawals · requested</option>
                <option value="withdrawal_paid">Withdrawals · paid</option>
                <option value="withdrawal_rejected">Withdrawals · rejected</option>
                <option value="manual_payout">Manual payouts</option>
                <option value="stripe_deposit">Stripe deposits</option>
                <option value="crypto_deposit">Crypto deposits</option>
                <option value="topup">Top-ups</option>
                <option value="wallet_set">Wallet · set</option>
                <option value="wallet_changed">Wallet · changed</option>
                <option value="benefit_redeemed">Benefits</option>
                <option value="physical_card">Physical card</option>
                <option value="ambassador_promoted">Ambassador promotions</option>
              </select>
              <button
                data-testid="admin-activity-refresh"
                onClick={() => loadActivity(token)}
                className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-white/70 hover:border-gold hover:text-gold"
              >
                <RefreshCw className="h-3 w-3" /> Refresh
              </button>
            </div>
          </div>
          <p className="mt-1 text-[11px] leading-relaxed text-white/45">
            Every client action lands here: logins, trades, deposits, boost claims, withdrawals, wallet changes, benefits redeemed, physical card requests and admin actions. Auto-refreshes every 15 seconds.
          </p>
          <div className="mt-4 max-h-[440px] overflow-y-auto" data-testid="admin-activity-list">
            {activityRows === null ? (
              <p className="text-xs text-white/30">Loading feed…</p>
            ) : activityRows.length === 0 ? (
              <p className="text-xs text-white/30" data-testid="admin-activity-empty">No activity yet — the feed will populate the moment clients act.</p>
            ) : (
              <ul className="space-y-2">
                {activityRows.map((a) => {
                  const kindTone = ({
                    trade: "text-[#00FF41] border-[#00FF41]/25",
                    boost_claimed: "text-gold border-gold/40",
                    withdrawal_requested: "text-[#FF9F1C] border-[#FF9F1C]/40",
                    withdrawal_paid: "text-[#00FF41] border-[#00FF41]/40",
                    withdrawal_rejected: "text-[#FF003C] border-[#FF003C]/40",
                    manual_payout: "text-[#00FF41] border-[#00FF41]/25",
                    stripe_deposit: "text-[#26A69A] border-[#26A69A]/40",
                    crypto_deposit: "text-[#26A69A] border-[#26A69A]/40",
                    topup: "text-gold border-gold/30",
                    login: "text-white/60 border-white/15",
                    wallet_set: "text-white/70 border-white/20",
                    wallet_changed: "text-[#FF9F1C] border-[#FF9F1C]/30",
                    benefit_redeemed: "text-gold border-gold/30",
                    physical_card: "text-white/75 border-white/20",
                    ambassador_promoted: "text-gold border-gold/50",
                  })[a.kind] || "text-white/60 border-white/15";
                  return (
                    <li
                      key={a.id}
                      data-testid={`admin-activity-row-${a.id}`}
                      className="grid grid-cols-[110px_1fr_auto] items-center gap-3 border-b border-white/5 pb-2 text-xs last:border-b-0"
                    >
                      <span className={`inline-flex justify-center rounded-full border px-2 py-0.5 text-[9px] uppercase tracking-[0.18em] ${kindTone}`}>
                        {a.kind.replace(/_/g, " ")}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-white/85" data-testid={`admin-activity-msg-${a.id}`}>{a.message}</p>
                        <p className="mt-0.5 truncate font-tabular text-[10px] text-white/40">
                          {a.code || "—"}
                          {a.email ? ` · ${a.email}` : ""}
                        </p>
                      </div>
                      <span className="whitespace-nowrap font-tabular text-[10px] text-white/40">
                        {a.created_at ? new Date(a.created_at).toLocaleString("en-GB", { hour12: false }) : ""}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>



        <div className="mt-10 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="border border-gold/30 bg-gold/[0.04] p-6" data-testid="admin-daily-code-card">
            <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Daily boost code</p>
            <p className="mt-2 font-tabular text-3xl tracking-[0.15em] text-white" data-testid="admin-daily-code">
              {daily?.code || "…"}
            </p>
            <p className="mt-2 text-[11px] text-white/45" data-testid="admin-daily-code-meta">
              {daily
                ? `${daily.date} · ${daily.redemptions} redemption(s) · ${daily.is_active ? "active" : "disabled"}` +
                  (daily.broadcast_at
                    ? ` · emailed to ${daily.broadcast_sent ?? 0}${daily.broadcast_failed ? ` (${daily.broadcast_failed} failed)` : ""}`
                    : " · auto-emails 08:00 Dubai")
                : ""}
            </p>
            {!liveLimited ? (
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  data-testid="admin-daily-broadcast"
                  onClick={broadcastNow}
                  disabled={busy || !daily}
                  className="rounded-full bg-gold px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-gold-light disabled:opacity-40"
                >
                  Send now
                </button>
                <button
                  data-testid="admin-daily-regenerate"
                  onClick={regenerateCode}
                  disabled={busy}
                  className="rounded-full border border-gold/40 px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-ink disabled:opacity-40"
                >
                  New code
                </button>
                <button
                  data-testid="admin-daily-toggle"
                  onClick={toggleCode}
                  disabled={busy || !daily}
                  className="rounded-full border border-white/15 px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] text-white/60 transition-colors duration-300 hover:border-gold hover:text-gold disabled:opacity-40"
                >
                  {daily?.is_active ? "Disable" : "Enable"}
                </button>
              </div>
            ) : (
              <p className="mt-4 text-[10px] uppercase tracking-[0.2em] text-white/30" data-testid="admin-live-limited-note">
                Read-only on the live build · full controls after publish
              </p>
            )}
          </div>

          <div className="border border-white/10 bg-surface p-6" data-testid="admin-withdrawals-card">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/35">Withdrawal requests · TRC-20</p>
            {wdRows === null ? (
              <p className="mt-4 text-xs text-white/30">Loading…</p>
            ) : wdRows.length === 0 ? (
              <p className="mt-4 text-xs text-white/30" data-testid="admin-withdrawals-empty">No withdrawal requests.</p>
            ) : (
              <div className="mt-4 space-y-2" data-testid="admin-withdrawals-list">
                {wdRows.slice(0, 6).map((w) => (
                  <div key={w.wid} data-testid={`admin-wd-row-${w.wid}`} className="border-b border-white/5 pb-2.5 text-xs last:border-b-0">
                    <div className="flex items-center justify-between">
                      <span className="font-tabular text-gold/80">{w.code}</span>
                      <span className="font-tabular text-white">${(w.amount ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      <span className={w.status === "paid" ? "text-[#00FF41]" : w.status === "rejected" ? "text-[#FF003C]" : "text-gold"}>
                        {w.status}
                      </span>
                      {w.status === "pending" && (
                        <span className="flex gap-1.5">
                          <button
                            data-testid={`admin-wd-paid-${w.wid}`}
                            onClick={() => setWdStatus(w.wid, "paid")}
                            className="rounded-full border border-[#00FF41]/40 px-3 py-1 text-[9px] uppercase tracking-[0.15em] text-[#00FF41] hover:bg-[#00FF41]/10"
                          >
                            Paid
                          </button>
                          <button
                            data-testid={`admin-wd-rejected-${w.wid}`}
                            onClick={() => setWdStatus(w.wid, "rejected")}
                            className="rounded-full border border-[#FF003C]/40 px-3 py-1 text-[9px] uppercase tracking-[0.15em] text-[#FF003C] hover:bg-[#FF003C]/10"
                          >
                            Reject
                          </button>
                        </span>
                      )}
                    </div>
                    <p className="mt-1 font-tabular text-[10px] text-white/40" data-testid={`admin-wd-wallet-${w.wid}`}>
                      {w.wallet || "no wallet"} · {w.created_at ? new Date(w.created_at).toLocaleDateString() : "—"}
                    </p>
                    {w.payout_txid && (
                      <p className="mt-1 font-tabular text-[10px] text-[#00FF41]" data-testid={`admin-wd-txid-${w.wid}`}>
                        Paid ·{" "}
                        <a
                          href={`https://tronscan.org/#/transaction/${w.payout_txid}`}
                          target="_blank"
                          rel="noreferrer"
                          className="underline hover:text-gold"
                        >
                          {w.payout_txid.slice(0, 10)}…{w.payout_txid.slice(-8)}
                        </a>
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 border border-gold/25 bg-surface p-6" data-testid="admin-manual-payout-card">
          <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Manual payout · already sent externally</p>
          <p className="mt-1 text-[11px] leading-relaxed text-white/45">
            Use this when you paid a client USDT from your wallet directly. Debits the exact amount from their terminal balance, logs a paid record and emails them a Tronscan receipt.
          </p>
          <form onSubmit={submitManualPayout} className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_120px_auto]">
            <input
              data-testid="admin-manual-code"
              value={manualForm.code}
              onChange={(e) => setManualForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
              placeholder="AUR-XXXXXXXX"
              className="border border-white/15 bg-ink px-3 py-2.5 font-tabular text-xs text-white placeholder:text-white/25 focus:border-gold focus:outline-none"
              required
            />
            <input
              data-testid="admin-manual-amount"
              type="number"
              step="0.01"
              min="0.01"
              value={manualForm.amount}
              onChange={(e) => setManualForm((f) => ({ ...f, amount: e.target.value }))}
              placeholder="Amount USD"
              className="border border-white/15 bg-ink px-3 py-2.5 font-tabular text-xs text-white placeholder:text-white/25 focus:border-gold focus:outline-none"
              required
            />
            <button
              data-testid="admin-manual-submit"
              type="submit"
              disabled={manualBusy || !manualForm.code || !manualForm.amount}
              className="flex items-center justify-center gap-2 rounded-full bg-gold px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-gold-light disabled:opacity-40"
            >
              {manualBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : "Debit & log"}
            </button>
          </form>
          <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <input
              data-testid="admin-manual-wallet"
              value={manualForm.wallet}
              onChange={(e) => setManualForm((f) => ({ ...f, wallet: e.target.value.trim() }))}
              placeholder="Wallet paid to (optional — will use saved TRC-20)"
              className="border border-white/10 bg-ink px-3 py-2.5 font-tabular text-[11px] text-white/80 placeholder:text-white/25 focus:border-gold focus:outline-none"
            />
            <input
              data-testid="admin-manual-txid"
              value={manualForm.txid}
              onChange={(e) => setManualForm((f) => ({ ...f, txid: e.target.value.trim() }))}
              placeholder="TRC-20 TXID (optional — enables Tronscan link)"
              className="border border-white/10 bg-ink px-3 py-2.5 font-tabular text-[11px] text-white/80 placeholder:text-white/25 focus:border-gold focus:outline-none"
            />
          </div>
          {manualMsg && (
            <p className="mt-3 rounded border border-[#00FF41]/30 bg-[#00FF41]/5 p-2.5 text-[11px] text-[#00FF41]" data-testid="admin-manual-success">
              {manualMsg}
            </p>
          )}
          {manualErr && (
            <p className="mt-3 rounded border border-[#FF003C]/30 bg-[#FF003C]/5 p-2.5 text-[11px] text-[#FF003C]" data-testid="admin-manual-error">
              {manualErr}
            </p>
          )}
        </div>

        <div className="mt-4 border border-white/10 bg-surface p-6" data-testid="admin-digest-card">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Daily WhatsApp digest · 20:00 Dubai</p>
              <p className="mt-1 text-[11px] leading-relaxed text-white/45">
                Auto-sends every day at 20:00 Dubai time to {"+971505049962"} with today's withdrawals, per-user profits, new tiers and top-ups. Use the buttons to preview or fire now.
              </p>
            </div>
            <div className="flex gap-2">
              <button
                data-testid="admin-digest-preview"
                onClick={previewDigest}
                disabled={digestBusy}
                className="rounded-full border border-white/15 px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-white/70 hover:border-gold hover:text-gold disabled:opacity-40"
              >
                {digestBusy ? "…" : "Preview"}
              </button>
              <button
                data-testid="admin-digest-send"
                onClick={sendDigestNow}
                disabled={digestBusy}
                className="rounded-full bg-gold px-4 py-2 text-[10px] uppercase tracking-[0.2em] text-ink hover:bg-gold-light disabled:opacity-40"
              >
                {digestBusy ? "…" : "Send now"}
              </button>
            </div>
          </div>
          {digestMsg && (
            <p className="mt-3 rounded border border-[#00FF41]/30 bg-[#00FF41]/5 p-2.5 text-[11px] text-[#00FF41]" data-testid="admin-digest-success">
              {digestMsg}
            </p>
          )}
          {digestErr && (
            <p className="mt-3 rounded border border-[#FF003C]/30 bg-[#FF003C]/5 p-2.5 text-[11px] text-[#FF003C]" data-testid="admin-digest-error">
              {digestErr}
            </p>
          )}
          {digestPreview && (
            <div className="mt-3 space-y-2" data-testid="admin-digest-preview-list">
              {(digestPreview.messages || []).map((m, i) => (
                <pre key={i} className="whitespace-pre-wrap rounded border border-white/10 bg-ink p-3 font-tabular text-[11px] leading-relaxed text-white/75">
{m}
                </pre>
              ))}
            </div>
          )}
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="border border-white/10 bg-surface p-6" data-testid="admin-rate-card">
            <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Today's boost rate · admin only</p>
            <p className="mt-1 text-[11px] leading-relaxed text-white/45">
              Chosen systematically each day — cannot be overridden. Users never see this number.
            </p>
            <p className="mt-3 font-tabular text-3xl text-gold" data-testid="admin-rate-effective">
              {rateInfo ? `${(rateInfo.effective_rate*100).toFixed(2)}%` : "…"}
            </p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40">
              {rateInfo ? `Band ${(rateInfo.min*100).toFixed(1)}% – ${(rateInfo.max*100).toFixed(1)}%` : ""}
            </p>
          </div>

          <div className="border border-[#FF003C]/20 bg-surface p-6" data-testid="admin-purge-card">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#FF003C]">Clean payment log</p>
            <p className="mt-1 text-[11px] leading-relaxed text-white/45">
              Removes every abandoned Stripe checkout, junk row and non-paid transaction so your admin log only shows real paying users.
            </p>
            <button
              data-testid="admin-purge-btn"
              onClick={purgeAbandoned}
              disabled={purgeBusy}
              className="mt-4 rounded-full border border-[#FF003C]/40 px-5 py-2.5 text-[10px] uppercase tracking-[0.2em] text-[#FF003C] hover:bg-[#FF003C]/10 disabled:opacity-40"
            >
              {purgeBusy ? "Purging…" : "Purge abandoned"}
            </button>
            {purgeMsg && (
              <p className="mt-2 text-[11px] text-white/60" data-testid="admin-purge-msg">{purgeMsg}</p>
            )}
          </div>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="admin-stats">
          {[
            { label: "Users", value: showcase ? SHOWCASE_STATS.total_users : usersData?.stats?.total_users, testid: "admin-stat-users" },
            { label: "Paid customers", value: showcase ? SHOWCASE_STATS.paid_customers : usersData?.stats?.paid_customers, testid: "admin-stat-customers" },
            {
              label: showcase ? "Available" : "Revenue",
              value: showcase
                ? `$${(SHOWCASE_STATS.total_available / 1e6).toFixed(1)}M`
                : usersData ? `$${(usersData.stats.total_revenue ?? 0).toLocaleString()}` : null,
              testid: "admin-stat-revenue",
            },
            { label: "Pending payouts", value: showcase ? SHOWCASE_STATS.pending_withdrawals : usersData?.stats?.pending_withdrawals, testid: "admin-stat-payouts" },
          ].map((s) => (
            <div key={s.label} className="border border-white/10 bg-surface p-5">
              <p className="text-[10px] uppercase tracking-[0.25em] text-white/35">{s.label}</p>
              <p className="mt-2 font-tabular text-2xl text-white" data-testid={s.testid}>
                {s.value ?? "…"}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-4 border border-white/10 bg-surface" data-testid="admin-users-card">
          <div className="border-b border-white/10 px-5 py-3">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/35">Users · who joined</p>
          </div>
          {usersData === null && !showcase ? (
            <p className="px-5 py-6 text-xs text-white/30">Loading…</p>
          ) : (showcase ? DEMO_USERS : usersData.users).length === 0 ? (
            <p className="px-5 py-6 text-xs text-white/30" data-testid="admin-users-empty">No users yet.</p>
          ) : (
            (showcase ? DEMO_USERS : usersData.users).map((u) => (
              <div
                key={u.code}
                data-testid={`admin-user-row-${u.code}`}
                className="grid grid-cols-[1.4fr_1fr_auto] items-center gap-3 border-b border-white/5 px-5 py-3 text-xs last:border-b-0 sm:grid-cols-[1.4fr_1fr_0.8fr_0.8fr_0.8fr_auto]"
              >
                <span className="min-w-0">
                  <span className="block truncate text-white/85">{u.email || "—"}</span>
                  <span className="block font-tabular text-[10px] text-gold/70">{u.code}</span>
                </span>
                <span className="hidden font-tabular text-white/60 sm:block">
                  {u.tier_paid ? `$${u.tier_paid.toLocaleString()} tier` : "—"}
                </span>
                <span className="hidden font-tabular text-white/85 sm:block" data-testid={`admin-user-balance-${u.code}`}>
                  {u.balance_usd == null ? "—" : `$${u.balance_usd.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                </span>
                <span className="hidden text-white/50 sm:block">
                  {u.daily_redemptions == null ? "—" : `${u.daily_redemptions} boost${u.daily_redemptions === 1 ? "" : "s"}`}
                </span>
                <span className="font-tabular text-white/60">
                  {u.referral_count > 0 ? `${u.referral_count} ref · $${u.referral_earned.toLocaleString()}` : "—"}
                </span>
                <span className={`text-[9px] uppercase tracking-[0.15em] ${u.auto_trade ? "text-[#00FF41]" : "text-white/25"}`}>
                  {u.auto_trade ? "Auto" : ""}
                </span>
              </div>
            ))
          )}
        </div>

        <div className="mt-6 border border-white/10 bg-surface" data-testid="admin-benefits-card">
          <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
            <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Concierge benefit choices</p>
            <span className="text-[10px] uppercase tracking-[0.2em] text-white/35">
              {benefitRows ? `${benefitRows.length} request(s)` : "…"}
            </span>
          </div>
          {benefitRows === null ? (
            <p className="px-5 py-6 text-xs text-white/30">Loading…</p>
          ) : benefitRows.length === 0 ? (
            <p className="px-5 py-6 text-xs text-white/30" data-testid="admin-benefits-empty">No concierge choices yet.</p>
          ) : (
            benefitRows.map((r) => (
              <div
                key={r.id}
                data-testid={`admin-benefit-row-${r.id}`}
                className="grid grid-cols-1 items-start gap-3 border-b border-white/5 px-5 py-4 text-xs last:border-b-0 md:grid-cols-[1fr_1fr_auto]"
              >
                <div className="min-w-0">
                  <p className="text-white/85">
                    {r.benefit_title} <span className="text-white/45">· {r.value || "—"}</span>
                  </p>
                  <p className="mt-1 truncate text-[10px] text-white/50">{r.email || r.code}</p>
                </div>
                <div>
                  <p className="text-gold">{r.partner}</p>
                  {r.note && <p className="mt-1 text-[10px] italic text-white/50">"{r.note}"</p>}
                </div>
                <span className="font-tabular text-[10px] text-white/40">
                  {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                </span>
              </div>
            ))
          )}
        </div>

        <div className="mt-6 border border-white/10 bg-surface" data-testid="admin-crypto-card">
          <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
            <p className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-gold">
              <Bitcoin className="h-3.5 w-3.5" /> Crypto payments · TRC-20 USDT
            </p>
            <span className="text-[10px] uppercase tracking-[0.2em] text-white/35">
              {cryptoRows ? `${cryptoRows.filter((r) => r.status === "pending").length} pending · ${cryptoRows.length} total` : "…"}
            </span>
          </div>
          {cryptoRows === null ? (
            <p className="px-5 py-6 text-xs text-white/30">Loading…</p>
          ) : cryptoRows.length === 0 ? (
            <p className="px-5 py-6 text-xs text-white/30" data-testid="admin-crypto-empty">
              No crypto submissions yet.
            </p>
          ) : (
            cryptoRows.map((r) => {
              const isTopup = !!r.topup_access_code;
              const pending = r.status === "pending";
              const credited = r.status === "credited";
              const rejected = r.status === "rejected";
              const verifyReason = r.verify_last?.reason;
              return (
                <div
                  key={r.id}
                  data-testid={`admin-crypto-row-${r.id}`}
                  className="grid grid-cols-1 items-start gap-3 border-b border-white/5 px-5 py-4 text-xs last:border-b-0 md:grid-cols-[1fr_auto]"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <span className="font-tabular text-white">
                        ${Number(r.amount ?? 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                      <span className="text-white/45">→ tier ${Number(r.target_tier ?? 0).toLocaleString()}</span>
                      <span className="text-[10px] uppercase tracking-[0.15em] text-gold/70">
                        {isTopup ? `top-up · ${r.topup_access_code}` : "new access"}
                      </span>
                      {r.auto_verified && (
                        <span className="rounded-full border border-[#00FF41]/40 px-2 py-0.5 text-[9px] uppercase tracking-[0.15em] text-[#00FF41]">
                          Auto-verified
                        </span>
                      )}
                      <span
                        className={
                          credited ? "text-[#00FF41]" : rejected ? "text-[#FF003C]" : "text-gold"
                        }
                        data-testid={`admin-crypto-status-${r.id}`}
                      >
                        {r.status}
                      </span>
                    </div>
                    <p className="mt-1 truncate text-white/60">{r.email}</p>
                    <div className="mt-1 flex items-center gap-2 font-tabular text-[10px] text-white/40">
                      <span className="truncate" data-testid={`admin-crypto-txid-${r.id}`}>
                        TX · {r.txid?.slice(0, 12)}…{r.txid?.slice(-8)}
                      </span>
                      <a
                        href={`https://tronscan.org/#/transaction/${r.txid}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-white/50 hover:text-gold"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </a>
                      <button
                        onClick={() => navigator.clipboard?.writeText(r.txid || "")}
                        className="text-white/50 hover:text-gold"
                        title="Copy TXID"
                      >
                        <Copy className="h-3 w-3" />
                      </button>
                      <span>· {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}</span>
                    </div>
                    {credited && r.access_code && (
                      <p className="mt-1 font-tabular text-[10px] text-gold/70">
                        Access · {r.access_code}
                      </p>
                    )}
                    {verifyReason && (
                      <p className="mt-1 text-[10px] text-white/40" data-testid={`admin-crypto-verify-${r.id}`}>
                        Chain check · {verifyReason}
                      </p>
                    )}
                  </div>
                  {pending && (
                    <div className="flex flex-wrap gap-1.5">
                      <button
                        data-testid={`admin-crypto-recheck-${r.id}`}
                        onClick={() => cryptoAction(r.id, "recheck")}
                        disabled={cryptoBusy !== null}
                        className="flex items-center gap-1 rounded-full border border-white/15 px-3 py-1 text-[9px] uppercase tracking-[0.15em] text-white/70 hover:border-gold hover:text-gold disabled:opacity-40"
                      >
                        {cryptoBusy === `${r.id}:recheck` ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <RefreshCw className="h-3 w-3" />
                        )}
                        Re-check
                      </button>
                      <button
                        data-testid={`admin-crypto-approve-${r.id}`}
                        onClick={() => cryptoAction(r.id, "approve")}
                        disabled={cryptoBusy !== null}
                        className="rounded-full border border-[#00FF41]/40 px-3 py-1 text-[9px] uppercase tracking-[0.15em] text-[#00FF41] hover:bg-[#00FF41]/10 disabled:opacity-40"
                      >
                        {cryptoBusy === `${r.id}:approve` ? "…" : "Approve"}
                      </button>
                      <button
                        data-testid={`admin-crypto-reject-${r.id}`}
                        onClick={() => cryptoAction(r.id, "reject")}
                        disabled={cryptoBusy !== null}
                        className="rounded-full border border-[#FF003C]/40 px-3 py-1 text-[9px] uppercase tracking-[0.15em] text-[#FF003C] hover:bg-[#FF003C]/10 disabled:opacity-40"
                      >
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        <div className="mt-6 border border-white/10 bg-surface" data-testid="admin-transactions-table">
          <div className="grid grid-cols-2 gap-4 border-b border-white/10 px-5 py-3 text-[10px] uppercase tracking-[0.25em] text-white/35 sm:grid-cols-6">
            <span>Date (UTC)</span>
            <span className="hidden sm:inline">Amount</span>
            <span>Status</span>
            <span className="hidden sm:inline">Access code</span>
            <span className="hidden sm:inline">Email</span>
            <span className="hidden sm:inline">Receipt sent</span>
          </div>
          {!rows ? (
            <p className="px-5 py-10 text-center text-xs text-white/30" data-testid="admin-loading">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="px-5 py-10 text-center text-xs text-white/30" data-testid="admin-empty">No payments yet.</p>
          ) : (
            rows.map((r) => (
              <div
                key={r.session_id}
                data-testid={`admin-row-${r.session_id.slice(-6)}`}
                className="grid grid-cols-2 items-center gap-4 border-b border-white/5 px-5 py-3.5 text-xs last:border-b-0 sm:grid-cols-6"
              >
                <span className="font-tabular text-white/50">
                  {r.created_at ? new Date(r.created_at).toLocaleString() : "—"}
                </span>
                <span className="hidden font-tabular text-white sm:inline">
                  ${(r.amount ?? 0).toLocaleString()} {String(r.currency || "usd").toUpperCase()}
                </span>
                <span
                  className={
                    r.payment_status === "paid"
                      ? "text-[#00FF41]"
                      : r.payment_status === "pending"
                        ? "text-gold"
                        : "text-[#FF003C]"
                  }
                  data-testid={`admin-status-${(r.session_id || r.id || "").slice(-6)}`}
                >
                  {r.payment_status}
                </span>
                <span className="hidden font-tabular text-gold/80 sm:inline">{r.access_code || "—"}</span>
                <span className="hidden truncate text-white/50 sm:inline">{r.email || "—"}</span>
                <span className="hidden text-white/40 sm:inline">{r.email_sent ? "Yes" : "No"}</span>
              </div>
            ))
          )}
        </div>
        {error && (
          <p className="mt-4 text-xs text-[#FF003C]" data-testid="admin-load-error">{error}</p>
        )}
      </div>
    </div>
  );
}
